/**
 * Type Definitions for Collections API
 *
 * Collections are datasets with detection settings.
 * This module defines types for the V4 Records API, and the plain-language copy
 * the monitor surfaces share.
 */

import type { Record } from '../../types/record';
import type { FilterLogic } from '../../components/v4/records/filter/types/filter.types';

// =============================================================================
// Collection Types
// =============================================================================

/**
 * Monitoring status for a collection (Monitor surface)
 */
export type MonitoringStatus = 'active' | 'paused';

/** A monitor's health as the backend derives it; `ok` renders nothing. */
export type MonitorHealthStatus = 'ok' | 'warning' | 'critical';

/** The statuses a surface renders, each in its own tone. */
export type UnhealthyStatus = Exclude<MonitorHealthStatus, 'ok'>;

/** The condition behind a status that is not `ok`. */
export type HealthReason =
  | 'no_advance'
  | 'cap_streak'
  | 'overlap_streak'
  | 'admission_streak'
  | 'run_failed'
  | 'reconciliation_gap'
  | 'dead_letters'
  | 'reaped';

/** A line's label and the hint that follows it. */
export interface MonitorCopy {
  label: string;
  hint: string;
}

/**
 * The words for each reason, verbatim from ingestion-integrity-architecture.md §7.2 and
 * read by every surface, so a reason never reaches a user as its code. A mapped type
 * rather than `Record`, which in this module is the signal record.
 */
export const HEALTH_REASON_COPY: { [R in HealthReason]: MonitorCopy } = {
  no_advance: {
    label: 'Not making progress',
    hint: 'The last 6 runs fetched records but could not move past them. A record may be failing every time.',
  },
  cap_streak: {
    label: 'Falling behind',
    hint: 'The last 3 runs stopped before catching up. Raise the record limit or run more often.',
  },
  overlap_streak: {
    label: 'Runs are overrunning',
    hint: 'Each run is still going when the next is due. Run less often, or lower the record limit.',
  },
  admission_streak: {
    label: 'Runs are being held back',
    hint: 'Recent runs were skipped because classification is backed up. New records land as it clears; if it has not cleared within a few hours, an admin should check the classification workers.',
  },
  run_failed: {
    label: 'Import is failing',
    hint: 'Recent runs or daily completeness checks could not reach the source. An admin should check the source connection.',
  },
  reconciliation_gap: {
    label: 'Records may be missing',
    hint: 'The source reports more records than were imported for a recent day. An admin can rescan that day.',
  },
  dead_letters: {
    label: 'Some records could not be imported',
    hint: 'Records that failed repeatedly are held for review. Nothing else is blocked.',
  },
  reaped: {
    label: 'A run was interrupted',
    hint: 'The last run stopped unexpectedly and was closed. The next run resumes from where it got to.',
  },
};

/** The line a paused collection shows under its monitor summary, in the neutral tone. */
export const MONITORING_PAUSED_COPY: MonitorCopy = {
  label: 'Monitoring paused',
  hint: 'Nothing new is imported until monitoring is resumed.',
};

const UNKNOWN_REASON_LABEL = 'Monitor needs attention';

/** What a surface renders for a monitor that is not healthy. */
export interface MonitorHealthCopy {
  status: UnhealthyStatus;
  label: string;
  /** Null for a reason the map does not hold. */
  hint: string | null;
}

/**
 * The health a surface shows, or null when there is none to show: at `ok`, with no
 * status, or with a status that has no tone. A reason the map does not hold as its own
 * key reads as needing attention, with no hint.
 */
export const monitorHealthCopy = (
  status?: string | null,
  reason?: string | null,
): MonitorHealthCopy | null => {
  if (status !== 'warning' && status !== 'critical') return null;
  if (reason && Object.prototype.hasOwnProperty.call(HEALTH_REASON_COPY, reason)) {
    const { label, hint } = HEALTH_REASON_COPY[reason as HealthReason];
    return { status, label, hint };
  }
  return { status, label: UNKNOWN_REASON_LABEL, hint: null };
};

/**
 * Source-monitor summary for a collection fed by an import job.
 * Absent for manually-curated collections.
 */
export interface CollectionSourceSummary {
  monitorId: string;
  monitorName: string;
  /** Skill identifier (e.g. "intercom-reporting"). Frontend maps to a display label. */
  skillName: string;
  /** Cadence id from the wizard's spec vocabulary (e.g. "every_1h", "daily"). */
  cadence: string;
  monitorStatus: string;
  lastRunAt?: string | null;
  nextRunAt?: string | null;
  /** Absent from an API older than the health columns, which reads as `ok`. */
  healthStatus?: MonitorHealthStatus;
  /** A string, not `HealthReason`: a newer API may send a code the map does not hold. */
  healthReason?: string | null;
  /** When the current status and reason began: ISO 8601 in UTC with no offset. */
  healthSince?: string | null;
}

/**
 * Collection (Dataset) entity from API
 */
export interface Collection {
  id: string;
  name: string;
  type: string;
  organizationId: string;
  complaintDetectionEnabled: boolean;
  vulnerabilityDetectionEnabled: boolean;
  status: string;
  /** Creator/owner user id — the owner may manage (rename/delete) their own
   *  collection even without the manage_collection capability. */
  ownedBy?: string | null;
  /** Resolved creator display name (null when unresolvable). */
  ownedByName?: string | null;
  /** Owning team (the data scope) + its resolved name. Null for legacy rows
   *  without an owning team. */
  ownerTeamId?: string | null;
  ownerTeamName?: string | null;
  recordCount?: number;
  averageSeverity?: number | null;
  monitoringStatus?: MonitoringStatus;
  eligibleComplaintsCount?: number;
  vulnerableCustomersCount?: number;
  createdAt: string;
  updatedAt: string;
  /**
   * Set when an import monitor targets this collection. Carries the source
   * skill + cadence so the Monitor Conduct cards can show
   * "Intercom · Every 1h · Last synced 12m ago" without a second API call.
   */
  source?: CollectionSourceSummary | null;
}

/**
 * Collection statistics
 */
export interface CollectionStats {
  totalRecords: number;
  avgConductScore: number;
  dissatisfactionRatio: number;
  vulnerabilityExposure: number;
  eligibleComplaintsRatio: number;
}

/**
 * Collection with stats response
 */
export interface CollectionWithStats {
  collection: Collection;
  stats: CollectionStats;
  /**
   * Whether the collection holds TCF detections the caller could filter to.
   * Only the collection-detail response carries it, because answering costs a
   * query and depends on the caller's scope.
   */
  hasTcfData: boolean;
}

/**
 * Collection list response
 */
export interface CollectionListResponse {
  collections: Collection[];
  total: number;
}

/**
 * Collection filters
 */
export interface CollectionFilters {
  type?: string;
  status?: string;
}

/**
 * Collection update request
 */
export interface CollectionUpdateRequest {
  name?: string;
  complaintDetectionEnabled?: boolean;
  vulnerabilityDetectionEnabled?: boolean;
}

// =============================================================================
// Records Types
// =============================================================================

/**
 * Pagination metadata from API
 */
export interface PaginationMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

/**
 * Breakdown item for category statistics
 */
export interface BreakdownItem {
  label: string;
  value: number; // Percentage (0-100)
}

/**
 * Aggregate statistics for the entire filtered recordset
 */
export interface AggregateStats {
  totalRecords: number;
  avgConductScore: number;
  dissatisfactionRatio: number;
  vulnerabilityExposure: number;
  eligibleComplaintsRatio: number;
  vulnerableCount: number;
  eligibleComplaintsCount: number;
  // Category breakdowns (top 3 for each)
  dissatisfactionByCategory: BreakdownItem[];
  vulnerabilityByCategory: BreakdownItem[];
  eligibleComplaintsByConfidence: BreakdownItem[];
  rootCausesByCategory: BreakdownItem[];
  topRootCauseLabels: LabelCountItem[];
}

export interface LabelCountItem {
  label: string;
  count: number;
}

/**
 * Records list response (paginated with aggregate stats)
 */
export interface RecordsListResponse {
  records: Record[];
  pagination: PaginationMeta;
  aggregateStats: AggregateStats;
}

/**
 * Date range preset options for Global Filter Strip
 */
export type DateRangePreset = 'last_7_days' | 'last_30_days' | 'last_quarter' | 'custom';

/**
 * Conduct dimensions for filtering
 */
export type ConductDimension = 'dissatisfaction' | 'strain' | 'harm' | 'tcf';

/**
 * Regulatory detection flags for filtering
 */
export type RegulatoryFlag = 'vulnerability' | 'complaint';

/**
 * Record filters for querying
 */
export interface RecordFilters {
  search?: string;
  conductScoreMin?: number;
  conductScoreMax?: number;
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  includeDrafts?: boolean;
  eligibleComplaintsOnly?: boolean; // Filter to only eligible complaints
  vulnerableOnly?: boolean; // Filter to only vulnerable customers
  sortBy?: 'created_at' | 'conduct_score' | 'severity_score' | 'customer_name' | 'received_at';
  sortOrder?: 'asc' | 'desc';

  // Global Filter Strip fields (GO-35)
  dateRangePreset?: DateRangePreset;
  dimensions?: ConductDimension[];
  dimensionsLogic?: FilterLogic;
  regulatoryFlags?: RegulatoryFlag[];
  regulatoryFlagsLogic?: FilterLogic;
}

/**
 * Confirm complaint response
 */
export interface ConfirmComplaintResponse {
  id: string;
  isConfirmedComplaint: boolean;
  confirmedAt: string;
  confirmedBy: string;
}

/**
 * Reclassify record response
 */
export interface ReclassifyRecordResponse {
  signal_id: string;
  /** The same three answers `ReclassifyResponse` carries: this path reaches the
   *  same endpoint, and a closed literal here would let a v4 surface report a
   *  refusal as a started run. */
  status: 'reclassification_started' | 'already_classifying' | 'import_pending';
  message: string;
}
